import React, {useEffect, useState} from 'react';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {
  apiGetAllFarmTeamRoles,
  apiCreateFdFarmTeam,
  apiDeleteFdFarmTeam,
} from 'constants/Api';
import {FdTeamForm, TeamSubmission} from 'components/applications/FdTeamForm';
import {ISupplierUserProfile, IGenericType} from 'constants/interfaces';
import {CircularProgress, Typography} from '@material-ui/core';

const AddOrUpdateFdTeam = (props: {
  user: ISupplierUserProfile;
  applicationId: number;
  initialTeam: any[];
}) => {
  const [roles, setRoles] = useState<{id: number; name: string}[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [rolesError, setRolesError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  /* ── load dropdown roles ───────────────────────────── */
  useEffect(() => {
    setLoadingRoles(true);
    apiGetAllFarmTeamRoles()
      .then((list: IGenericType[]) => {
        const mappedRoles = list.map((r) => ({
          id: Number(r.id),
          name: r.name ?? '',
        }));
        console.log('Farm team roles loaded:', mappedRoles);
        setRoles(mappedRoles);
      })
      .catch((err) => {
        console.error('Failed to load farm team roles:', err);
        setRolesError('Failed to load team roles. Please try again.');
      })
      .finally(() => setLoadingRoles(false));
  }, []);

  /* ── auto-close when success ───────────────────────── */
  useEffect(() => {
    if (success) {
      reduxSetSidePanelEditState('saved');
      setTimeout(() => {
        reduxToggleSidePanel(false);
      }, 100);
    }
  }, [success]);

  /* ── save handler ──────────────────────────────────── */
  const submit = async (data: TeamSubmission) => {
    // Prevent submission if roles haven't loaded
    if (roles.length === 0) {
      alert('Team roles are not loaded. Please refresh and try again.');
      return;
    }

    setSaving(true);
    setSubmitErrors([]);

    const existingById = new Map(
      props.initialTeam.map((t) => [Number(t.id), t]),
    );

    const submittedIds = new Set(
      data.members.filter((m) => m.id).map((m) => Number(m.id)),
    );

    // Find members to delete (in initial but not in submitted)
    const deleteRows = props.initialTeam.filter(
      (t) => !submittedIds.has(Number(t.id)),
    );

    const createRows = data.members.filter((m) => !m.id);
    const updateRows = data.members.filter((m) => {
      if (!m.id) return false;
      const orig = existingById.get(Number(m.id));
      return (
        !orig ||
        orig.name !== m.name ||
        Number(orig.farmTeamRole.id) !== Number(m.farmTeamRoleId)
      );
    });

    console.log('Farm team save operation:', {
      deleteRows,
      createRows,
      updateRows,
    });
    // Helper to format error messages robustly
    const toMessage = (err: any): string => {
      try {
        if (!err) return 'Unknown error';
        if (typeof err === 'string') return err;
        if (err.message) return err.message as string;
        // Apollo-style
        if (err?.graphQLErrors?.length)
          return err.graphQLErrors.map((e: any) => e.message).join('; ');
        if (err?.response?.errors?.length)
          return err.response.errors.map((e: any) => e.message).join('; ');
        return JSON.stringify(err);
      } catch (e) {
        return String(err);
      }
    };

    // Phase 1: deletions (sequential group)
    const badIdDeletes = deleteRows
      .filter((t) => Number.isNaN(Number(t.id)))
      .map((t) => `${t.id}`);
    if (badIdDeletes.length) {
      const msg = `Cannot delete members with non-numeric ids: [${badIdDeletes.join(
        ', ',
      )}]`;
      console.error(msg);
      setSubmitErrors((prev) => [...prev, msg]);
    }
    const deletePayloads = deleteRows
      .filter((t) => !Number.isNaN(Number(t.id)))
      .map((t) => Number(t.id));
    if (deletePayloads.length) {
      console.log('Deleting farm team members:', deletePayloads);
      const delResults = await Promise.allSettled(
        deletePayloads.map((id) => apiDeleteFdFarmTeam(id)),
      );
      const delErrors = delResults
        .filter((r) => r.status === 'rejected')
        .map((r) => toMessage((r as PromiseRejectedResult).reason));
      if (delErrors.length) {
        setSaving(false);
        setSubmitErrors((prev) => [
          ...prev,
          'Failed to delete one or more team members:',
          ...delErrors,
        ]);
        return; // stop here; user can retry
      }
    }

    // Phase 2: creates and updates (run together)
    const createPayloads = createRows.map((m) => ({
      name: m.name,
      farmTeamRoleId: Number(m.farmTeamRoleId),
      applicationId: Number(props.applicationId),
    }));
    const updatePayloads = updateRows.map((m) => ({
      id: Number(m.id),
      name: m.name,
      farmTeamRoleId: Number(m.farmTeamRoleId),
      applicationId: Number(props.applicationId),
    }));
    console.log('Creating farm team members:', createPayloads);
    console.log('Updating farm team members:', updatePayloads);

    const upsertResults = await Promise.allSettled([
      ...createPayloads.map((p) => apiCreateFdFarmTeam(p)),
      ...updatePayloads.map((p) => apiCreateFdFarmTeam(p)),
    ]);

    const upsertErrors = upsertResults
      .filter((r) => r.status === 'rejected')
      .map((r) => toMessage((r as PromiseRejectedResult).reason));

    if (upsertErrors.length) {
      setSaving(false);
      setSubmitErrors((prev) => [
        ...prev,
        'Failed to save one or more team members:',
        ...upsertErrors,
      ]);
      return;
    }

    setSaving(false);
    setSuccess(true);
  };

  const close = () => reduxToggleSidePanel(false, null);

  /* ── early return when success ───────────────────────── */
  if (success) {
    return null;
  }

  /* ── loading roles ─────────────────────────────────── */
  if (loadingRoles) {
    return (
      <div style={{padding: '2rem', textAlign: 'center'}}>
        <CircularProgress />
        <Typography style={{marginTop: '1rem'}}>
          Loading team roles...
        </Typography>
      </div>
    );
  }

  /* ── error loading roles ───────────────────────────── */
  if (rolesError) {
    return (
      <div style={{padding: '2rem', textAlign: 'center'}}>
        <Typography color="error">{rolesError}</Typography>
        <button onClick={close} style={{marginTop: '1rem'}}>
          Close
        </button>
      </div>
    );
  }

  /* ── form view ─────────────────────────────────────── */
  return (
    <FdTeamForm
      roles={roles}
      defaultMembers={props.initialTeam.map((t) => ({
        id: Number(t.id),
        name: t.name,
        farmTeamRoleId: t.farmTeamRole.id,
      }))}
      isLoading={saving}
      submitButtonTitle="Save"
      onSubmit={submit}
      onCancel={close}
      submitErrors={submitErrors}
    />
  );
};

export default AddOrUpdateFdTeam;
